import { Injectable } from '@angular/core';
import { SalesforceDataSourceApiService} from 'datasource-service-lib';
import { IEntityMetadata, IEntityMetadataService } from 'interfaces-lib';
import { from, Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, tap, toArray } from 'rxjs/operators';

interface IMetadataRequestEntity {
  name: string;
  fields: string[];
  iconUrl?: string;
  iconColor?: string;
}

@Injectable({ providedIn: 'root' })
export class SalesforceEntityMetadataService implements IEntityMetadataService {
  private _cache: IEntityMetadata[] | null = null;

  private entities: IMetadataRequestEntity[] = [
    { name: 'TR1__Closing_Report__c', fields: ['id'] },
    { name: 'Contact', fields: ['id'] },
    { name: 'TR1__Job__c', fields: ['id'] },
    { name: 'Account', fields: ['id'] },
    { name: 'TR1__Application_V2__c', fields: ['id'] },
  ];

  private fallbackEntities = this.entities;

  constructor(private apiService: SalesforceDataSourceApiService) {}

  getEntitiesMetadata(): Observable<IEntityMetadata[]> {
    if (this._cache && this._cache.length) {
      return of(this._cache);
    }

    return this.fetchEntitiesMetadata().pipe(tap((data) => (this._cache = data))) as Observable<IEntityMetadata[]>;
  }

  // @TODO: remove as any stuff
  private fetchEntitiesMetadata(): Observable<IEntityMetadata[]> {
    // Try to load entity types dynamically from datasource service
    return this.apiService.getAvailableEntityTypes().pipe(
      map((entityTypes) => entityTypes.map((et) => ({
        name: et.objectName,
        fields: ['id'],
        iconUrl: et.iconUrl,
        iconColor: et.iconColor,
      }))),
      mergeMap((entities: IMetadataRequestEntity[]) => {
        if (!entities.length) {
          return of([]);
        }
        return from(entities).pipe(
          mergeMap((entity) => this.fetchEntityMetadata(entity), entities.length),
          filter((entity): entity is IEntityMetadata => entity !== null),
          toArray(),
          map((data: IEntityMetadata[]) => this.processMetadata(data))
        );
      }),
      catchError((error) => {
        console.error('Failed to load dynamic entity types for metadata, using fallback', error);
        return this.fetchFallbackMetadata();
      })
    );
  }

  private fetchFallbackMetadata(): Observable<IEntityMetadata[]> {
    return from(this.fallbackEntities).pipe(
      mergeMap((entity) => this.fetchEntityMetadata(entity), this.fallbackEntities.length),
      filter((entity): entity is IEntityMetadata => entity !== null),
      toArray(),
      map((data) => this.processMetadata(data))
    );
  }

  private fetchEntityMetadata(entity: IMetadataRequestEntity): Observable<IEntityMetadata | null> {
    return this.apiService.meta({ entity: entity.name, fields: entity.fields }).pipe(
      map((metadata) => {
        if (!metadata) {
          return null;
        }
        const mergedMetadata: IEntityMetadata = { ...metadata };
        if (entity.iconUrl?.trim()) {
          mergedMetadata.iconUrl = entity.iconUrl.trim();
        }
        if (entity.iconColor?.trim()) {
          mergedMetadata.iconColor = entity.iconColor.trim();
        }
        return mergedMetadata;
      }),
      catchError((error) => {
        console.error(`Failed to load metadata for entity ${entity.name}`, error);
        return of(null);
      })
    );
  }

  private processMetadata(data: IEntityMetadata[]): IEntityMetadata[] {
    const result: IEntityMetadata[] = [];
    data.forEach((meta) => {
      if (meta) {
        result.push(meta);
      }
    });
    return result;
  }
}
